import { useContext } from 'react';
import { AuthContext } from '@/context/auth.context';
import { useViewMode } from '@/context/view-mode.context';
import { useCan } from '@/hooks/useCan';
import { STAFF_SECTIONS_PERMISSION } from '@/pages/authenticated/settings/organization/organizationSettingsSections';
import { isAdmin } from '@/utils/user';
import type CurrentUser from 'src/types/CurrentUser';

/**
 * Whether the viewer may manage the staff-only ("gentlyOnly") Manage
 * Organization sections: Gently staff (view-mode aware) OR a holder of
 * `Update OrganizationSettings` (the Staff Admin group). The one client-side
 * definition of that gate; the backend mirror is
 * `canManageStaffSections` in `AuthorizationService`.
 *
 * Use it for a staff section's own data and actions. Do not use it for gates
 * that protect anything else (customer pages, imitation, account admin).
 */
export function useCanManageStaffSections(): boolean {
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const [holdsPermission] = useCan(
    STAFF_SECTIONS_PERMISSION.action,
    STAFF_SECTIONS_PERMISSION.subject,
  );
  return isAdmin(currentUser, viewMode) || holdsPermission;
}

/** Router-context variant of `useCanManageStaffSections`, for `beforeLoad`. */
export async function canManageStaffSectionsInRoute(context: {
  auth: { currentUser?: CurrentUser };
  authzClient: {
    can: (
      action: typeof STAFF_SECTIONS_PERMISSION.action,
      subject: typeof STAFF_SECTIONS_PERMISSION.subject,
    ) => Promise<boolean>;
  };
}): Promise<boolean> {
  return (
    isAdmin(context.auth.currentUser) ||
    (await context.authzClient.can(
      STAFF_SECTIONS_PERMISSION.action,
      STAFF_SECTIONS_PERMISSION.subject,
    ))
  );
}
